/**
 * FilterPill — canonical filter/chip pill component for filter/selection displays.
 *
 * Used in schedule filters to show selected disciplines, providers, patients, and other
 * filter criteria as removable pills.
 *
 * Color scheme by type:
 *   Discipline  → blue-100 (clinical focus)
 *   Provider    → green-100 (people/staff)
 *   Patient     → purple-100 (patient data)
 *   Other       → slate-100 (generic)
 *
 * Usage:
 *   <FilterPill type="discipline" text="RN" onRemove={() => ...} />
 *   <FilterPill type="provider" text="John Smith" onRemove={() => ...} />
 *   <FilterPill type="patient" text="Jane Doe" onRemove={() => ...} />
 */

import React from 'react';
import { X } from 'lucide-react';

// ─── Config type ──────────────────────────────────────────────────────────────

export interface FilterConfig {
  /** Light pill background (e.g. bg-blue-100) */
  bg: string;
  /** Pill text color (e.g. text-blue-800) */
  text: string;
  /** Pill border color (e.g. border-blue-200) */
  border: string;
  /** Hover background for remove button */
  hoverBg: string;
}

// ─── Canonical map ────────────────────────────────────────────────────────────

const FILTER_MAP: Record<string, FilterConfig> = {
  // ── Discipline (blue) ───────────────────────────────────────────────────────
  DISCIPLINE: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    hoverBg: 'hover:bg-blue-200',
  },

  // ── Provider (green) ────────────────────────────────────────────────────────
  PROVIDER: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    hoverBg: 'hover:bg-green-200',
  },

  // ── Patient (purple) ────────────────────────────────────────────────────────
  PATIENT: {
    bg: 'bg-purple-100',
    text: 'text-purple-800',
    border: 'border-purple-200',
    hoverBg: 'hover:bg-purple-200',
  },

  // ── Status (amber) ─────────────────────────────────────────────────────────
  STATUS: {
    bg: 'bg-amber-100',
    text: 'text-amber-800',
    border: 'border-amber-200',
    hoverBg: 'hover:bg-amber-200',
  },

  // ── Other/Default (slate) ──────────────────────────────────────────────────
  OTHER: {
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    border: 'border-slate-200',
    hoverBg: 'hover:bg-slate-200',
  },
};

const DEFAULT_CONFIG: FilterConfig = FILTER_MAP.OTHER;

// ─── Utility functions ────────────────────────────────────────────────────────

/**
 * Look up the canonical config for a filter type.
 * Normalises to uppercase; falls back to 'OTHER' for unknown types.
 */
export function getFilterConfig(type: string): FilterConfig {
  if (!type) return DEFAULT_CONFIG;
  const key = type.trim().toUpperCase();
  return FILTER_MAP[key] ?? DEFAULT_CONFIG;
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface FilterPillProps {
  /** Filter type: 'discipline' | 'provider' | 'patient' | 'status' | 'other' */
  type: string;
  /** Display text content */
  text: string;
  /** Called when remove button is clicked */
  onRemove: () => void;
  /** Whether the pill is disabled */
  disabled?: boolean;
  /** Extra Tailwind classes appended to the root element */
  className?: string;
}

/**
 * FilterPill — removable filter/chip badge component.
 *
 * Renders as: inline-flex, flex items-center, pill background, with closable X button.
 *
 * Standardized layout: inline-flex items-center · px-2 py-1 · rounded-full
 */
export function FilterPill({
  type,
  text,
  onRemove,
  disabled = false,
  className = '',
}: FilterPillProps) {
  const config = getFilterConfig(type);

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} ${config.border} border ${className}`.trim()}
    >
      <span className="truncate max-w-xs">{text}</span>
      <button
        onClick={onRemove}
        disabled={disabled}
        className={`ml-1 ${config.hoverBg} rounded-full p-0.5 flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        type="button"
        aria-label={`Remove ${text}`}
      >
        <X size={12} />
      </button>
    </span>
  );
}

export default FilterPill;
